import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/apiClient";
import { useConfig, payText } from "../lib/config";
import { Img, inr } from "../components/ui-bits";
import HeroScene from "../components/HeroScene";
import { Reveal, CountUp } from "../components/motion";
import { ArrowRight, Sprout, ShoppingBag, Sparkles, Leaf, TrendingUp, ShieldCheck, MapPin, UserPlus, Search, Handshake, Truck, MessageCircle, LineChart, Smartphone, Wheat, Droplets } from "lucide-react";

const CATEGORIES = [
  { k: "seeds", Icon: Sprout, label: "Seeds", sub: "Certified, high-yield", big: true },
  { k: "fertilizers", Icon: Leaf, label: "Fertilizers", sub: "Organic & balanced" },
  { k: "machinery", Icon: Truck, label: "Machinery", sub: "Tractors & tillers" },
  { k: "irrigation", Icon: Droplets, label: "Irrigation", sub: "Drip & sprinkler" },
  { k: "pesticides", Icon: ShieldCheck, label: "Crop care", sub: "Bio & chemical" },
  { k: "livestock", Icon: Wheat, label: "Livestock", sub: "Feed & supplements" },
];

const STEPS = {
  farmers: [
    { i: <UserPlus />, t: "Create your farm profile", d: "Add your location, size, certifications and photos so buyers can trust where food comes from." },
    { i: <Sprout />, t: "List your crops", d: "Post quantity, variety, harvest date and your expected price per kg." },
    { i: <Handshake />, t: "Get direct offers", d: "Buyers send procurement requests. Accept, reject or chat to agree the deal." },
  ],
  buyers: [
    { i: <Search />, t: "Find farms near the source", d: "Filter by crop, state, district and organic certification." },
    { i: <MessageCircle />, t: "Talk to the farmer", d: "Message the grower and send a procurement request with your price." },
    { i: <Truck />, t: "Shop farm inputs too", d: "Seeds, fertilizers and machinery from vendors, with order tracking." },
  ],
};


/** Photo that always has a rich fallback, so a slow or blocked image never leaves an empty box. */
function Photo({ src, alt = "", className = "", children }) {
  return (
    <div className={`relative overflow-hidden bg-gradient-to-br from-primary/80 via-primary to-[#0f2f26] ${className}`}>
      {src && <img src={src} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      {children}
    </div>
  );
}

/** Full-bleed band whose background moves slower than the page. */
function ParallaxBand({ children, className = "" }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : ["-14%", "14%"]);
  return (
    <section ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.div style={{ y }} className="absolute -inset-y-[16%] inset-x-0 bg-gradient-to-br from-[#123a2d] via-[#1B4D3E] to-[#0b251e]">
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0b251e]/70 via-[#0b251e]/45 to-[#0b251e]/75" />
      <div className="relative">{children}</div>
    </section>
  );
}

export default function Landing() {
  const { t } = useI18n();
  const cfg = useConfig();
  const ribbon = ["Seeds", "Fertilizers", "Machinery", "Irrigation", "Livestock", ...(cfg.market_data_enabled ? ["Live mandi prices"] : []), "Crop advisor", ...(cfg.ai_enabled ? ["Plant disease check"] : []), "Direct from farms", payText(cfg)];
  const chips = [
    ...(cfg.market_data_enabled ? [{ i: <LineChart className="w-5 h-5" />, t: "Live mandi prices", d: "Government market rates by state", cls: "top-2 right-0 w-72", delay: "0s" }] : []),
    { i: <Smartphone className="w-5 h-5" />, t: payText(cfg), d: cfg.payment_methods?.includes("upi") ? "Scan, pay, upload - verified fast" : "Pay when your order arrives", cls: "top-40 left-0 w-72", delay: "1.2s" },
    { i: <Wheat className="w-5 h-5" />, t: "Crop advisor", d: "Best crops for your soil & season", cls: "bottom-0 right-6 w-72", delay: "2.4s" },
  ];
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [farms, setFarms] = useState([]);
  const [tab, setTab] = useState("farmers");

  useEffect(() => {
    api.get("/stats", { silent: true }).then(r => setStats(r.data)).catch(() => {});
    api.get("/catalog", { params: { limit: 4 }, silent: true }).then(r => setProducts(r.data.items)).catch(() => {});
    api.get("/farms", { params: { limit: 3 }, silent: true }).then(r => setFarms(r.data)).catch(() => {});
  }, []);

  const statItems = stats ? [
    { v: stats.farms, l: stats.farms === 1 ? "Farm listed" : "Farms listed" },
    { v: stats.products, l: stats.products === 1 ? "Product in store" : "Products in store" },
    { v: stats.buyers, l: stats.buyers === 1 ? "Registered buyer" : "Registered buyers" },
  ].filter(s => s.v > 0) : [];

  return (
    <div className="bg-grain -mb-12">
      {/* ---------------- HERO ---------------- */}
      <HeroScene>
        <div className="max-w-7xl mx-auto w-full px-6 pt-20 pb-40 md:pb-48">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 text-white">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass-chip text-xs font-medium mb-7">
                <Sparkles className="w-3.5 h-3.5" /> {t("badge_hero")}
              </motion.div>
              <motion.h1 data-testid="hero-title" initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                         className="font-display text-5xl sm:text-6xl md:text-7xl xl:text-[5.5rem] font-bold leading-[1.02] tracking-tight">
                {t("hero_line1")}<br /><span className="text-shimmer">{t("hero_line2")}</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }}
                        className="mt-6 text-lg md:text-xl text-white/85 max-w-xl leading-relaxed">
                {t("hero_tail")}
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }} className="mt-9 flex flex-wrap gap-3">
                <Link to="/marketplace"><Button data-testid="hero-browse" size="lg" className="h-12 px-7 bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-lg shadow-black/25 btn-shine">
                  <ShoppingBag className="w-4 h-4 mr-2" />{t("cta_browse")}
                </Button></Link>
                <Link to="/signup"><Button data-testid="hero-join" size="lg" variant="outline" className="h-12 px-7 glass-chip text-white border-white/30 hover:bg-white/20 hover:text-white btn-shine">
                  <Sprout className="w-4 h-4 mr-2" />{t("cta_join")}
                </Button></Link>
              </motion.div>
              {statItems.length > 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.7 }} className="mt-12 flex flex-wrap gap-x-12 gap-y-5">
                  {statItems.map((s) => (
                    <div key={s.l}>
                      <div className="font-display text-3xl md:text-4xl font-bold"><CountUp value={s.v} /></div>
                      <div className="text-xs text-white/70 mt-1 tracking-wide">{s.l}</div>
                    </div>
                  ))}
                </motion.div>
              )}
            </div>

            {/* floating glass cards */}
            <div className="hidden lg:block lg:col-span-5 relative h-[28rem]">
              {chips.map((c) => (
                <div key={c.t} className={`absolute ${c.cls} floaty`} style={{ animationDelay: c.delay }}>
                  <div className="glass-chip rounded-2xl p-4 text-white flex gap-3 items-start">
                    <div className="w-10 h-10 rounded-xl bg-white/20 grid place-items-center shrink-0">{c.i}</div>
                    <div><div className="font-semibold text-sm">{c.t}</div><div className="text-xs text-white/75 mt-0.5">{c.d}</div></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </HeroScene>

      {/* ---------------- RIBBON ---------------- */}
      <div className="marquee bg-primary text-primary-foreground overflow-hidden border-y border-white/10" aria-hidden>
        <div className="marquee-track py-4">
          {[...ribbon, ...ribbon].map((x, i) => (
            <span key={i} className="flex items-center text-sm tracking-wide whitespace-nowrap">
              <span className="px-7">{x}</span><Leaf className="w-3.5 h-3.5 text-secondary" />
            </span>
          ))}
        </div>
      </div>

      {/* ---------------- CATEGORIES (bento) ---------------- */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal className="flex items-end justify-between mb-10 gap-4">
          <div>
            <div className="eyebrow mb-3">{t("sec_store")}</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold">{t("sec_store_h")}</h2>
          </div>
          <Link to="/marketplace" className="text-sm text-primary font-medium hidden sm:inline-flex items-center gap-1 group">
            View all <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 md:grid-rows-2 gap-4 md:h-[34rem]">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.k} delay={i * 0.07} className={c.big ? "col-span-2 md:row-span-2" : ""}>
              <Link to={`/marketplace?category=${c.k}`} data-testid={`cat-${c.k}`} className="group block h-full">
                <Photo alt="" className="rounded-2xl h-full min-h-[11rem] md:min-h-0 lift">
                  <c.Icon className={`absolute right-4 top-4 text-white/15 ${c.big ? "w-40 h-40" : "w-20 h-20"}`} aria-hidden />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b251e]/85 via-[#0b251e]/20 to-transparent transition-opacity" />
                  <div className="absolute inset-0 scale-100 group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                    <div className={`font-display font-bold ${c.big ? "text-3xl" : "text-xl"}`}>{t("c_" + c.k)}</div>
                    <div className="text-xs text-white/75 mt-0.5 flex items-center gap-1">{c.sub}<ArrowRight className="w-3 h-3 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" /></div>
                  </div>
                </Photo>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- PARALLAX STATEMENT ---------------- */}
      <ParallaxBand>
        <div className="max-w-5xl mx-auto px-6 py-32 md:py-44 text-center text-white">
          <Reveal><div className="eyebrow !text-[#f1c27d] mb-5">Our belief</div></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl sm:text-5xl md:text-7xl font-bold leading-[1.05]">Every harvest deserves<br />a <span className="text-shimmer">fair price</span>.</h2></Reveal>
          <Reveal delay={0.2}><p className="mt-7 text-lg text-white/80 max-w-2xl mx-auto">No listing fees. No layers of middlemen. Farmers set the price, buyers see the source, and everyone gets a clearer deal.</p></Reveal>
          <Reveal delay={0.3}><Link to="/farmers"><Button size="lg" className="mt-9 h-12 px-8 bg-white text-primary hover:bg-white/90">Meet the farms<ArrowRight className="w-4 h-4 ml-2" /></Button></Link></Reveal>
        </div>
      </ParallaxBand>

      {/* ---------------- FEATURED PRODUCTS ---------------- */}
      {products.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24">
          <Reveal className="flex items-end justify-between mb-10 gap-4">
            <div><div className="eyebrow mb-3">{t("popular")}</div><h2 className="font-display text-4xl md:text-5xl font-bold">{t("fresh_store")}</h2></div>
            <Link to="/marketplace" className="text-sm text-primary font-medium inline-flex items-center gap-1 group">{t("view_all")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></Link>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <Link to={`/product/${p.id}`}>
                  <Card className="overflow-hidden lift border-border/50 h-full group">
                    <div className="aspect-square bg-muted overflow-hidden"><Img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
                    <div className="p-4 md:p-5">
                      <Badge variant="outline" className="text-[10px] mb-2 capitalize">{p.category}</Badge>
                      <h3 className="font-medium line-clamp-2 text-sm md:text-base min-h-[2.5rem]">{p.name}</h3>
                      <div className="mt-3 font-display text-xl font-bold text-primary">{inr(p.price)} <span className="text-xs font-sans font-normal text-muted-foreground">/ {p.unit}</span></div>
                    </div>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- WHY ULUME ---------------- */}
      <section className="relative py-24 bg-gradient-to-b from-accent/60 to-background">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="text-center mb-14"><div className="eyebrow mb-3">Why ULUME</div><h2 className="font-display text-4xl md:text-5xl font-bold">Built on trust, from soil to shelf</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { i: <Leaf />, t: "Know your source", d: "Farms publish their location, size and certifications, so buyers see exactly where produce comes from." },
              { i: <TrendingUp />, t: "Fewer middlemen", d: "Farmers set their asking price and negotiate directly with buyers. No listing fees." },
              { i: <ShieldCheck />, t: "Orders you can track", d: `${payText(cfg)}, and follow every order from packing to doorstep.` },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 0.1}>
                <Card className="p-8 border-border/50 lift h-full relative overflow-hidden">
                  <div className="absolute -top-6 -right-4 font-display text-[7rem] font-bold text-primary/[0.05] leading-none select-none">{i + 1}</div>
                  <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground grid place-items-center mb-5 shadow-lg shadow-primary/25">{v.i}</div>
                  <h3 className="font-display font-semibold text-xl mb-2">{v.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{v.d}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- FARMS ---------------- */}
      {farms.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24">
          <Reveal className="flex items-end justify-between mb-10 gap-4">
            <div><div className="eyebrow mb-3">Growers</div><h2 className="font-display text-4xl md:text-5xl font-bold">{t("meet_farms")}</h2></div>
            <Link to="/farmers" className="text-sm text-primary font-medium inline-flex items-center gap-1 group">All farms <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></Link>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {farms.map((f, i) => (
              <Reveal key={f.user_id} delay={i * 0.1}>
                <Link to={`/farmers/${f.user_id}`}>
                  <Card className="overflow-hidden lift border-border/50 h-full group">
                    <Photo src={f.photos?.[0]} alt={f.farm_name} className="aspect-[4/3]">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      {f.certifications?.includes("organic") && <Badge className="absolute top-3 left-3 bg-white/90 text-primary hover:bg-white"><Leaf className="w-3 h-3 mr-1" />Organic</Badge>}
                    </Photo>
                    <div className="p-6">
                      <h3 className="font-display text-2xl font-bold">{f.farm_name}</h3>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1.5"><MapPin className="w-3 h-3" />{f.district}, {f.state}</p>
                      <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{f.description}</p>
                    </div>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- HOW IT WORKS ---------------- */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <Reveal className="text-center mb-12">
          <div className="eyebrow mb-3">Simple by design</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold">{t("how_works")}</h2>
          <div className="inline-flex mt-7 p-1.5 rounded-full bg-muted" role="tablist">
            {[["farmers", "I'm a farmer"], ["buyers", "I'm a buyer"]].map(([k, l]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${tab === k ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        </Reveal>
        <div className="relative grid md:grid-cols-3 gap-6">
          <div className="hidden md:block absolute top-[3.1rem] left-[16%] right-[16%] border-t-2 border-dashed border-primary/25" aria-hidden />
          {STEPS[tab].map((s, idx) => (
            <motion.div key={`${tab}-${s.t}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: idx * 0.1 }} className="relative text-center px-4">
              <div className="relative mx-auto w-[6.2rem] h-[6.2rem] rounded-full bg-background border-2 border-primary/20 grid place-items-center shadow-xl shadow-primary/10 mb-6">
                <div className="w-14 h-14 rounded-full bg-secondary/15 text-secondary grid place-items-center [&_svg]:w-6 [&_svg]:h-6">{s.i}</div>
                <span className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold grid place-items-center">{idx + 1}</span>
              </div>
              <h3 className="font-display font-semibold text-xl mb-2">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <ParallaxBand className="mx-0">
        <div className="max-w-4xl mx-auto px-6 py-28 text-center text-white">
          <Reveal><h2 className="font-display text-4xl md:text-6xl font-bold leading-tight">{t("cta_title")}</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-5 text-lg text-white/80">{t("cta_sub")}</p></Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Link to="/signup"><Button size="lg" data-testid="cta-signup" className="h-12 px-8 bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-lg shadow-black/30">{t("get_started_free")}</Button></Link>
              <Link to="/farmers"><Button size="lg" variant="outline" className="h-12 px-8 glass-chip text-white border-white/30 hover:bg-white/20 hover:text-white">{t("explore_farms")}</Button></Link>
            </div>
          </Reveal>
        </div>
      </ParallaxBand>
    </div>
  );
}
